import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardProvider, useDashboard } from './DashboardContext';
import CourseViewer from '../../components/CourseViewer';
import TutorialTour from '../../components/TutorialTour';
import { communityApi } from '../../services/api';
import {
  LayoutDashboard,
  Radio,
  UserPlus,
  User,
  TrendingUp,
  BookOpen,
  GraduationCap,
  CreditCard,
  Globe,
  Users,
  Settings,
  MonitorPlay,
  BarChart3,
  AlertTriangle,
  LogOut,
  Menu,
  X,
  UserCheck
} from 'lucide-react';
import '../Dashboard.css';

// Import all page components
import DashboardHome from './DashboardHome';
import ProfilePage from './ProfilePage';
import LiveStreamPage from './LiveStreamPage';
import PerformancePage from './PerformancePage';
import JournalPage from './JournalPage';
import MembershipPage from './MembershipPage';
import ForexFactoryPage from './ForexFactoryPage';
import TelegramCommunity from './TelegramCommunity';
import SettingsPage from './SettingsPage';
import StreamManagerPage from './StreamManagerPage';
import AdminDashboard from './AdminDashboard';
import CoursesPage from './CoursesPage';
import InvitePage from './InvitePage';
import MyCoachPage from './MyCoachPage';
import MediaRestorationPage from './MediaRestorationPage';

const DashboardContent = () => {
  const navigate = useNavigate();
  const {
    user,
    logout,
    activePage,
    setActivePage,
    showCourseViewer,
    setShowCourseViewer,
    viewingCourseId,
    setViewingCourseId,
    streamData
  } = useDashboard();

  const [showTutorial, setShowTutorial] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Close mobile menu when page changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [activePage]);

  // Fetch unread counts when user is available and periodically
  useEffect(() => {
    if (!user) return;
    
    const fetchUnread = async () => {
      try {
        const data = await communityApi.getUnreadCounts();
        setUnreadCounts(data.channels || {});
      } catch (error) {
        // Non-fatal: unread counts will retry on the next interval tick
        console.warn('Failed to fetch unread counts:', error);
      }
    };
    
    // Fetch immediately
    fetchUnread();
    
    // Then fetch every 30 seconds
    const interval = setInterval(fetchUnread, 30 * 1000);
    
    return () => clearInterval(interval);
  }, [user]);

  // Mark messages as read when viewing Community page
  useEffect(() => {
    if (!user || activePage !== 'Community') return;
    
    const markAllChannelsRead = async () => {
      try {
        // Get all channels and mark them as read
        const channelNames = Object.keys(unreadCounts);
        for (const channel of channelNames) {
          if (unreadCounts[channel] > 0) {
            await communityApi.markChannelRead(channel);
          }
        }
        // Clear unread counts after marking as read
        setUnreadCounts({});
      } catch (error) {
        console.error('Failed to mark channels as read:', error);
      }
    };
    
    // Small delay to ensure the page has loaded
    const timeout = setTimeout(markAllChannelsRead, 500);
    return () => clearTimeout(timeout);
  }, [activePage, user]);

  // Calculate total unread for Community
  const totalUnread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);

  // Check if tutorial should be shown (first time after onboarding)
  useEffect(() => {
    if (user?.onboarding_completed) {
      // Check both localStorage and a session flag to determine if tutorial should show
      const tutorialCompleted = localStorage.getItem('hyperdrive_tutorial_completed');
      const tutorialShownThisSession = sessionStorage.getItem('hyperdrive_tutorial_shown');
      
      if (!tutorialCompleted && !tutorialShownThisSession) {
        // Small delay to let the dashboard render first
        const timer = setTimeout(() => {
          setShowTutorial(true);
          sessionStorage.setItem('hyperdrive_tutorial_shown', 'true');
        }, 800);
        return () => clearTimeout(timer);
      }
    }
  }, [user?.onboarding_completed]);

  const handleTutorialComplete = () => {
    localStorage.setItem('hyperdrive_tutorial_completed', 'true');
    setShowTutorial(false);
  };

  const handleTutorialSkip = () => {
    localStorage.setItem('hyperdrive_tutorial_completed', 'true');
    setShowTutorial(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', page: 'Dashboard', tourId: 'dashboard' },
    { icon: User, label: 'Profile', page: 'Profile', tourId: 'profile' },
    { icon: Radio, label: 'Live Stream', page: 'Live Stream', tourId: 'live-stream' },
    { icon: Globe, label: 'ForexFactory', page: 'ForexFactory', tourId: 'forex' },
    { icon: TrendingUp, label: 'Performance', page: 'Performance', tourId: 'performance' },
    { icon: UserCheck, label: 'My Coach', page: 'My Coach', tourId: 'my-coach' },
    { icon: Users, label: 'Community', page: 'Community', tourId: 'community' },
    { icon: GraduationCap, label: 'Courses', page: 'Courses', tourId: 'courses' },
    { icon: CreditCard, label: 'Membership', page: 'Membership', tourId: 'membership' },
    { icon: Settings, label: 'Settings', page: 'Settings', tourId: 'settings' },
  ];

  const adminItems = [
    { icon: BarChart3, label: 'Admin Panel', page: 'Admin Panel' },
    { icon: AlertTriangle, label: 'Media Restoration', page: 'Media Restoration' },
  ];

  const renderPageContent = () => {
    switch (activePage) {
      case 'Dashboard':
        return <DashboardHome />;
      case 'Profile':
        return <ProfilePage />;
      case 'Live Stream':
        return <LiveStreamPage />;
      case 'Performance':
        return <PerformancePage />;
      case 'Journal':
        return <JournalPage />;
      case 'Membership':
        return <MembershipPage />;
      case 'My Coach':
        return <MyCoachPage />;
      case 'ForexFactory':
        return <ForexFactoryPage />;
      case 'Community':
        return <TelegramCommunity />;
      case 'Settings':
        return <SettingsPage />;
      case 'Stream Manager':
        return <StreamManagerPage />;
      case 'Admin Panel':
        return <AdminDashboard />;
      case 'Media Restoration':
        return <MediaRestorationPage />;
      case 'Courses':
        return <CoursesPage />;
      default:
        return (
          <div className="page-content">
            <h1 className="page-title">{activePage}</h1>
            <p className="page-description">Coming soon...</p>
          </div>
        );
    }
  };

  return (
    <div className={`dashboard-layout ${mobileMenuOpen ? 'mobile-menu-open' : ''}`}>
      {/* Mobile Header */}
      <header className="mobile-header">
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
        <div className="mobile-brand" onClick={() => navigate('/')}>
          <img src={`${process.env.PUBLIC_URL}/logo-original.png`} alt="HDC Logo" className="mobile-logo" />
          <span>HyperDrive</span>
        </div>
        <div className="mobile-header-spacer"></div>
      </header>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-brand" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={`${process.env.PUBLIC_URL}/logo-original.png`} alt="HDC Logo" className="sidebar-logo" />
            <div className="sidebar-brand-text">
              <h1 className="sidebar-title">HyperDrive</h1>
              <p className="sidebar-subtitle">Trading Academy</p>
            </div>
          </div>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const showBadge = item.page === 'Community' && totalUnread > 0;
              const isLiveStream = item.page === 'Live Stream';
              const showLiveIndicator = isLiveStream && streamData?.isLive;
              return (
                <button
                  key={item.page}
                  className={`nav-item ${activePage === item.page ? 'active' : ''} ${showBadge ? 'has-unread' : ''} ${showLiveIndicator ? 'is-live' : ''}`}
                  onClick={() => setActivePage(item.page)}
                  data-tour={item.tourId}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                  {showBadge && (
                    <span className="sidebar-unread-badge">
                      {totalUnread > 99 ? '99+' : totalUnread}
                    </span>
                  )}
                  {showLiveIndicator && (
                    <span className="sidebar-live-badge">LIVE</span>
                  )}
                </button>
              );
            })}
          </div>

          {user?.role === 'admin' && (
            <div className="nav-section admin-section">
              <h3 className="section-title">Admin</h3>
              {adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.page}
                    className={`nav-item ${activePage === item.page ? 'active' : ''}`}
                    onClick={() => setActivePage(item.page)}
                  >
                    <Icon size={18} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </nav>

        <button onClick={handleLogout} className="sidebar-logout">
          <LogOut size={18} />
          <span>Logout</span>
        </button>
      </aside>

      <main className="dashboard-content">
        <div className="content-wrapper">
          {renderPageContent()}
        </div>
      </main>

      {/* Tutorial Tour */}
      {showTutorial && (
        <TutorialTour 
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
        />
      )}

      {/* Course Viewer - rendered outside main content for full-screen overlay */}
      {showCourseViewer && viewingCourseId && (
        <CourseViewer
          courseId={viewingCourseId}
          onClose={() => {
            setShowCourseViewer(false);
            setViewingCourseId(null);
          }}
        />
      )}
    </div>
  );
};

const Dashboard = () => {
  return (
    <DashboardProvider>
      <DashboardContent />
    </DashboardProvider>
  );
};

export default Dashboard;